import React, { useState, useEffect, useMemo, useRef } from "react";

import { presentMutationError } from "../../../../utils/showApiErrorToast";
import {
  BROKER_ALERT_TITLE_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

import {
  fetchNotificationTimezone,
  upsertNotificationTimezone,
  fetchNotificationPreferences,
  updateNotificationPreferences,
} from "../../../../services/notificationsApi";
import { DISPLAY_PREFERENCES_CHANGED_EVENT } from "../../../../hooks/useUserNotificationTimezone";
import { deviceIanaTimeZone, resolveUserReportingTimeZone } from "../../../../utils/reportingTimeZone";
import {
  clearCachedTimezoneSource,
  getDisplayTimezoneMode,
  readCachedTimezoneSource,
  writeCachedTimezoneSource,
} from "../../../../utils/displayTimezoneMode";
import { AccountSettingsFieldSelect } from "./AccountSettingsFieldSelect";
import { DisplayPreferenceUpdateRow } from "./DisplayPreferenceUpdateRow";
import SettingCard, {
  accountSettingsCardStackClassName,
  accountSettingsFieldClassName,
  accountSettingsFieldLabelStackClassName,
  accountSettingsFieldStackClassName,
} from "./SettingCard";
import { accountSettingsFieldDescriptionClassName } from "../accountSettingsCardShell";
import { TimezoneCombobox } from "@/components/TimezoneCombobox";

const DATE_FORMAT_OPTIONS = [
  { value: "MM/DD/YYYY", label: "MM/DD/YYYY (US)" },
  { value: "DD/MM/YYYY", label: "DD/MM/YYYY (EU)" },
  { value: "YYYY-MM-DD", label: "YYYY-MM-DD (ISO)" },
  { value: "MMM DD, YYYY", label: "MMM DD, YYYY (Long)" },
];

const NUMBER_FORMAT_OPTIONS = [
  { value: "0", label: "0 decimal places" },
  { value: "2", label: "2 decimal places" },
  { value: "4", label: "4 decimal places" },
  { value: "6", label: "6 decimal places" },
];

function dispatchDisplayPreferenceChange(setting: string): void {
  window.dispatchEvent(
    new CustomEvent(DISPLAY_PREFERENCES_CHANGED_EVENT, { detail: { setting } }),
  );
}

const DisplayPreferences: React.FC = () => {
  const deviceTimezone = deviceIanaTimeZone();

  const [dateFormat, setDateFormat] = useState<string>(() => {
    return localStorage.getItem("display_date_format") || "MM/DD/YYYY";
  });

  const [displayCurrency, setDisplayCurrency] = useState("USD");
  const [supportedCurrencies, setSupportedCurrencies] = useState<string[]>([]);
  const [displayCurrencyPrefsHydrated, setDisplayCurrencyPrefsHydrated] = useState(false);
  const [savingDisplayCurrency, setSavingDisplayCurrency] = useState(false);

  const [numberFormat, setNumberFormat] = useState<string>(() => {
    return localStorage.getItem("display_number_format") || "2";
  });

  const [timezone, setTimezone] = useState<string>(deviceTimezone);
  const [timezoneMode, setTimezoneMode] = useState<"auto" | "manual">(
    () => readCachedTimezoneSource() ?? "auto",
  );
  const [timezonePrefsHydrated, setTimezonePrefsHydrated] = useState(false);
  const [savingTimezoneMode, setSavingTimezoneMode] = useState(false);
  const [savingTimezone, setSavingTimezone] = useState(false);
  const timezoneFetchSeqRef = useRef(0);

  useEffect(() => {
    let mounted = true;
    const fetchSeq = ++timezoneFetchSeqRef.current;

    const loadTimezonePreference = async () => {
      try {
        const res = await fetchNotificationTimezone();
        if (!mounted || fetchSeq !== timezoneFetchSeqRef.current) return;
        const mode = getDisplayTimezoneMode(res, deviceTimezone);
        if (!readCachedTimezoneSource()) {
          writeCachedTimezoneSource(mode);
        }
        setTimezoneMode(mode);
        setTimezone(resolveUserReportingTimeZone(res.timezone, mode));
      } catch {
        if (!mounted || fetchSeq !== timezoneFetchSeqRef.current) return;
        const fallbackMode = readCachedTimezoneSource() ?? "auto";
        setTimezoneMode(fallbackMode);
        setTimezone(deviceTimezone);
      } finally {
        if (mounted && fetchSeq === timezoneFetchSeqRef.current) {
          setTimezonePrefsHydrated(true);
        }
      }
    };

    void loadTimezonePreference();

    return () => {
      mounted = false;
    };
  }, [deviceTimezone]);

  useEffect(() => {
    let mounted = true;

    const loadDisplayCurrency = async () => {
      try {
        const prefs = await fetchNotificationPreferences();
        if (!mounted) return;
        setDisplayCurrency((prefs.displayCurrency || "USD").toUpperCase());
        setSupportedCurrencies(
          prefs.supportedCurrencies?.length ? prefs.supportedCurrencies : ["USD"],
        );
      } catch {
        if (!mounted) return;
        setDisplayCurrency("USD");
        setSupportedCurrencies(["USD"]);
      } finally {
        if (mounted) setDisplayCurrencyPrefsHydrated(true);
      }
    };

    void loadDisplayCurrency();

    return () => {
      mounted = false;
    };
  }, []);

  const displayCurrencyOptions = useMemo(() => {
    const base = supportedCurrencies.length > 0 ? supportedCurrencies : ["USD"];
    const code = displayCurrency.toUpperCase();
    if (base.includes(code)) return base;
    return [code, ...base];
  }, [supportedCurrencies, displayCurrency]);

  const displayCurrencySelectOptions = useMemo(
    () =>
      displayCurrencyOptions.map((code) => {
        try {
          const name = new Intl.DisplayNames(["en"], { type: "currency" }).of(code);
          return { value: code, label: name ? `${code} — ${name}` : code };
        } catch {
          return { value: code, label: code };
        }
      }),
    [displayCurrencyOptions],
  );

  useEffect(() => {
    localStorage.setItem("display_date_format", dateFormat);
  }, [dateFormat]);

  useEffect(() => {
    localStorage.setItem("display_number_format", numberFormat);
  }, [numberFormat]);

  const handleTimezoneModeChange = async (useDeviceTimezone: boolean) => {
    const nextMode = useDeviceTimezone ? "auto" : "manual";
    const previousMode = timezoneMode;
    const previousCache = readCachedTimezoneSource();

    writeCachedTimezoneSource(nextMode);
    setTimezoneMode(nextMode);
    setSavingTimezoneMode(true);

    try {
      const res = await upsertNotificationTimezone({
        timezone: nextMode === "auto" ? deviceTimezone : timezone,
        source: nextMode,
        broker_account_id: null,
      });
      timezoneFetchSeqRef.current += 1;
      setTimezone(resolveUserReportingTimeZone(res.timezone, nextMode));
      toast.success("Timezone updated", {
        description:
          nextMode === "auto"
            ? `Using device timezone (${deviceTimezone}).`
            : `Manual timezone selected. Choose a zone and click Update.`,
      });
      dispatchDisplayPreferenceChange("timezone");
    } catch (error) {
      if (previousCache) {
        writeCachedTimezoneSource(previousCache);
      } else {
        clearCachedTimezoneSource();
      }
      setTimezoneMode(previousMode);
      toast.error("Failed to update timezone", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setSavingTimezoneMode(false);
    }
  };

  const handleUpdate = async (setting: string) => {
    if (setting === "displayCurrency") {
      setSavingDisplayCurrency(true);
      try {
        const current = await fetchNotificationPreferences();
        const updated = await updateNotificationPreferences({
          ...current,
          displayCurrency: displayCurrency.toUpperCase(),
        });
        setDisplayCurrency((updated.displayCurrency || "USD").toUpperCase());
        if (updated.supportedCurrencies?.length) {
          setSupportedCurrencies(updated.supportedCurrencies);
        }
        toast.success("Display currency updated", {
          description: `Amounts in the app will use ${(updated.displayCurrency || "USD").toUpperCase()} formatting.`,
        });
        dispatchDisplayPreferenceChange("displayCurrency");
      } catch (error) {
        presentMutationError(error, "Failed to update display currency");
      } finally {
        setSavingDisplayCurrency(false);
      }
      return;
    }

    if (setting === "timezone") {
      if (timezoneMode !== "manual") return;
      setSavingTimezone(true);
      try {
        const res = await upsertNotificationTimezone({
          timezone,
          source: "manual",
          broker_account_id: null,
        });
        timezoneFetchSeqRef.current += 1;
        writeCachedTimezoneSource("manual");
        setTimezoneMode("manual");
        setTimezone(resolveUserReportingTimeZone(res.timezone, "manual"));
        toast.success("Timezone updated", {
          description: `Using manual timezone (${resolveUserReportingTimeZone(res.timezone, "manual")}).`,
        });
        dispatchDisplayPreferenceChange("timezone");
      } catch (error) {
        presentMutationError(error, "Failed to update timezone");
      } finally {
        setSavingTimezone(false);
      }
      return;
    }

    dispatchDisplayPreferenceChange(setting);
  };

  return (
    <div
      className={accountSettingsCardStackClassName}
      data-account-settings-card-stack=""
      data-account-settings-display-preferences=""
    >
      <SettingCard
        title="Date Format"
        description="Choose how dates are displayed throughout the dashboard."
      >
        <DisplayPreferenceUpdateRow onUpdate={() => void handleUpdate("dateFormat")}>
          <AccountSettingsFieldSelect
            id="display-preferences-date-format"
            value={dateFormat}
            onValueChange={setDateFormat}
            options={DATE_FORMAT_OPTIONS}
          />
        </DisplayPreferenceUpdateRow>
      </SettingCard>

      <SettingCard
        title="Display currency"
        description="How monetary amounts are formatted in the app (symbol and decimals). This does not convert broker figures to another currency — it only changes presentation. The same preference is stored for notification emails on the server."
      >
        <DisplayPreferenceUpdateRow
          onUpdate={() => void handleUpdate("displayCurrency")}
          disabled={!displayCurrencyPrefsHydrated}
          updating={savingDisplayCurrency}
        >
          <AccountSettingsFieldSelect
            id="display-preferences-display-currency"
            value={displayCurrency}
            onValueChange={(value) => setDisplayCurrency(value.toUpperCase())}
            options={displayCurrencySelectOptions}
            disabled={!displayCurrencyPrefsHydrated || savingDisplayCurrency}
          />
        </DisplayPreferenceUpdateRow>
      </SettingCard>

      <SettingCard
        title="Decimal Places"
        description="Set the number of decimal places for displaying numbers."
      >
        <DisplayPreferenceUpdateRow onUpdate={() => void handleUpdate("numberFormat")}>
          <AccountSettingsFieldSelect
            id="display-preferences-number-format"
            value={numberFormat}
            onValueChange={setNumberFormat}
            options={NUMBER_FORMAT_OPTIONS}
          />
        </DisplayPreferenceUpdateRow>
      </SettingCard>

      <SettingCard
        title="Timezone"
        description="Used for trade log and statistics date ranges (by exit date), and for how entry/exit times are shown."
      >
        <div className={accountSettingsFieldStackClassName}>
          <Field className={accountSettingsFieldClassName}>
            <div className="flex items-center justify-between gap-3">
              <div
                data-account-settings-field-label-stack=""
                className={accountSettingsFieldLabelStackClassName}
              >
                <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>
                  Use device timezone automatically
                </FieldLabel>
                <FieldDescription className={accountSettingsFieldDescriptionClassName}>
                  Detected timezone: {deviceTimezone}
                </FieldDescription>
              </div>
              <Switch
                checked={timezoneMode === "auto"}
                disabled={!timezonePrefsHydrated}
                aria-busy={savingTimezoneMode || undefined}
                onCheckedChange={(checked) => void handleTimezoneModeChange(checked)}
              />
            </div>
          </Field>
          {timezonePrefsHydrated && timezoneMode === "manual" ? (
            <DisplayPreferenceUpdateRow
              onUpdate={() => void handleUpdate("timezone")}
              disabled={!timezonePrefsHydrated}
              updating={savingTimezone}
            >
              <TimezoneCombobox
                id="display-preferences-timezone"
                value={timezone}
                onValueChange={setTimezone}
                disabled={savingTimezone}
                variant="account-settings"
                aria-label="Display timezone"
              />
            </DisplayPreferenceUpdateRow>
          ) : null}
        </div>
      </SettingCard>
    </div>
  );
};

export default DisplayPreferences;
